import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarClock, ListChecks } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

export default function AdminWeekly() {
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);
  const { tf: t } = useLang();

  useEffect(() => { api.get("/public/weekly").then((r) => setW(r.data)).catch(() => {}); }, []);
  if (!w) return <div className="py-20 text-center text-ink-secondary">{t("adm_loading")}</div>;

  const set = (k, v) => setW({ ...w, [k]: v });
  const setCat = (i, k, v) => { const c = [...w.categories]; c[i] = { ...c[i], [k]: k === "percent" ? Number(v) : v }; set("categories", c); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/weekly", {
        studied: Number(w.studied), finalists: Number(w.finalists),
        next_selection: w.next_selection, categories: w.categories,
        // Paramètres automatiques du challenge hebdomadaire
        ideas_manual_base: Number(w.ideas_manual_base) || 0,
        ideas_per_hour: Number(w.ideas_per_hour ?? 1),
        presel_percent: Number(w.presel_percent ?? 7),
        winners_percent: Number(w.winners_percent ?? 10),
        countdown_mode: w.countdown_mode || "weekly",
      });
      toast.success(t("awk_saved"));
      // Recharger : les compteurs calculés (idées, présélectionnés, sélectionnées) se mettent à jour
      const r = await api.get("/public/weekly");
      setW(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  // Seuls « étudiées » et « finalistes » restent manuels : idées, présélectionnés et
  // sélectionnées sont calculés automatiquement à partir des paramètres ci-dessous.
  const fields = [
    ["studied", t("awk_studied")], ["finalists", t("awk_finalists")],
  ];
  const computed = [
    { v: w.ideas_count, l: t("awk_ideas") },
    { v: w.preselected, l: t("awk_presel") },
    { v: w.winners_count, l: t("awk_winners") },
  ];
  const dtLocal = w.next_selection ? new Date(w.next_selection).toISOString().slice(0, 16) : "";

  return (
    <div className="mx-auto max-w-3xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("awk_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("awk_title")}</h1>

      {/* Section « compteurs » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-compteurs">Compteurs</div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="weekly-panel-compteurs">
        {/* Valeurs calculées automatiquement — lecture seule */}
        <div className="sm:col-span-2 flex flex-wrap items-center gap-6 rounded-xl border border-white/10 bg-surface px-5 py-4" data-testid="weekly-computed">
          <span className="text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Calculés automatiquement :</span>
          {computed.map((c, i) => (
            <span key={i} className="flex items-baseline gap-2">
              <span className="font-display text-xl font-700 tabular-nums text-cyan">{c.v}</span>
              <span className="text-xs text-ink-secondary">{c.l}</span>
            </span>
          ))}
        </div>
        {fields.map(([k, l]) => (
          <label key={k} className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{l}</span>
            <input type="number" value={w[k]} onChange={(e) => set(k, e.target.value)} data-testid={`weekly-${k}`}
              className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
          </label>
        ))}
        <label className="block sm:col-span-2">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Compte à rebours</span>
          <select value={w.countdown_mode || "weekly"} onChange={(e) => set("countdown_mode", e.target.value)} data-testid="weekly-countdown-mode"
            className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan">
            <option value="weekly">Automatique — chaque lundi à 00:00 (7 jours, perpétuel)</option>
            <option value="fixed">Date personnalisée (ci-dessous)</option>
          </select>
        </label>
        {(w.countdown_mode || "weekly") === "fixed" && (
        <label className="block sm:col-span-2">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{t("awk_next_date")}</span>
          <input type="datetime-local" value={dtLocal} onChange={(e) => set("next_selection", new Date(e.target.value).toISOString())} data-testid="weekly-date"
            className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
        </label>
        )}

        <div className="sm:col-span-2 mt-2 rounded-xl border border-cyan/20 bg-cyan/5 p-4">
          <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-cyan">Compteur automatique (accueil, blog, challenge)</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Base d'idées au lundi (0 = projets réels de la semaine)</span>
              <input type="number" min="0" value={w.ideas_manual_base ?? 0} onChange={(e) => set("ideas_manual_base", e.target.value)} data-testid="weekly-ideas-manual-base"
                className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Progression par heure</span>
              <input type="number" min="0" value={w.ideas_per_hour ?? 1} onChange={(e) => set("ideas_per_hour", e.target.value)} data-testid="weekly-ideas-per-hour"
                className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Présélectionnés (% des idées)</span>
              <input type="number" min="0" max="100" step="0.5" value={w.presel_percent ?? 7} onChange={(e) => set("presel_percent", e.target.value)} data-testid="weekly-presel-percent"
                className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Sélectionnées (% des idées, min 1)</span>
              <input type="number" min="0" max="100" step="0.5" value={w.winners_percent ?? 10} onChange={(e) => set("winners_percent", e.target.value)} data-testid="weekly-winners-percent"
                className="w-full border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
            </label>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-ink-tertiary">
            Remise à zéro de tous les compteurs chaque lundi matin à 00h01, puis progression du montant choisi chaque heure.
            « Projets présélectionnés » = pourcentage des idées proposées affichées.
          </p>
        </div>
      </div>
      </>

      {/* Section « categories » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-categories">Catégories du challenge</div>
      <div className="mt-6" data-testid="weekly-panel-categories">
        <div className="space-y-3">
          {w.categories?.map((c, i) => (
            <div key={i} className="flex items-center gap-3">
              <input value={c.name} onChange={(e) => setCat(i, "name", e.target.value)} className="flex-1 border border-white/10 bg-surface px-3 py-2.5 text-sm text-white outline-none focus:border-cyan" />
              <input type="number" value={c.percent} onChange={(e) => setCat(i, "percent", e.target.value)} className="w-24 border border-white/10 bg-surface px-3 py-2.5 text-sm text-white outline-none focus:border-cyan" />
              <span className="text-ink-tertiary">%</span>
            </div>
          ))}
        </div>
      </div>
      </>

      <button onClick={save} disabled={saving} data-testid="weekly-save" className="aca-btn-primary mt-8">
        {saving ? t("sav_saving") : t("sav_save")}
      </button>
    </div>
  );
}
